import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import '../styles/preloader.css';

const LOADER_CONFIG = {
  emojiEnabled: false,       // Emoji animaties aan/uit
  loaderDelay: 2,            // Totale wachttijd in seconden (verkort van 5 naar 2)
  rotationRepeat: 0,         // Aantal rotatie herhalingen (verkort van 1 naar 0)
  mouseDistance: 400,        // Minimale muisafstand voor emoji
  emojiWaitTime: 500,        // Wacht tijd tussen emoji's
  emojiFallDelay: 200,       // Delay voor emoji val animatie
};

const emojiRotations = [90, -90];
const emojiSizes = [150, 200, 250, 300];
const totalEmojiVariants = 4;

export default function Preloader({ onComplete }) {
  const isLoadingRef = useRef(true);
  const lastMouseXRef = useRef(0);
  const lastMouseYRef = useRef(0);
  const lastEmojiTimeRef = useRef(0);

  useEffect(() => {
    // Blokkeer scrollen tijdens het laden
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Preload images on the page to determine actual load time
    const images = document.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
    });

    // Wait for images or use configured delay (whichever is shorter)
    const imageLoadPromise = Promise.all(imagePromises);
    const timeoutPromise = new Promise(resolve => setTimeout(resolve, LOADER_CONFIG.loaderDelay * 1000));
    
    Promise.race([imageLoadPromise, timeoutPromise]).then(() => {
      // Images loaded or timeout reached, proceed with loader animation
    });
    // Percentage teller animatie gebaseerd op loaderDelay
    const progressObj = { value: 0 };
    const progressEl = document.querySelector('.loader-progress');
    if (progressEl) {
      gsap.to(progressObj, {
        value: 100,
        duration: LOADER_CONFIG.loaderDelay,
        ease: 'linear',
        onUpdate: () => {
          const v = Math.min(100, Math.round(progressObj.value));
          progressEl.textContent = v + '%';
        }
      });
    }

    const createEmoji = (mouseX, mouseY) => {
      const emojisContainer = document.querySelector(".emojis");
      if (!emojisContainer) return Date.now();

      const emoji = document.createElement("div");
      const size = emojiSizes[Math.floor(Math.random() * emojiSizes.length)];
      const emojiVariant = Math.floor(Math.random() * totalEmojiVariants) + 1;

      emoji.className = "emoji";
      emoji.style.width = `${size}px`;
      emoji.style.height = `${size}px`;
      
      // Update pad naar emoji afbeeldingen
      emoji.style.backgroundImage = `url(/assets/loader-emojis/emoji-${emojiVariant}.png)`;
      emoji.style.left = `${mouseX - size / 2}px`;
      emoji.style.top = `${mouseY - size / 2}px`;

      emojisContainer.appendChild(emoji);

      const initialRotation =
        emojiRotations[Math.floor(Math.random() * emojiRotations.length)];

      const currentTime = Date.now();
      const delayFromLast =
        Math.max(0, LOADER_CONFIG.emojiFallDelay - (currentTime - lastEmojiTimeRef.current)) / 1000;

      gsap.set(emoji, {
        scale: 0,
        rotation: initialRotation,
      });

      gsap.timeline()
        .to(emoji, {
          scale: 1,
          rotation: 0,
          duration: 0.5,
          ease: "back.out(1.75)",
        })
        .to(emoji, {
          y: window.innerHeight + size,
          rotation: initialRotation,
          duration: 0.5,
          ease: "power2.in",
          delay: LOADER_CONFIG.emojiWaitTime / 1000 + delayFromLast,
          onComplete: () => emoji.remove(),
        });

      return currentTime;
    };

    const handleMouseMove = (e) => {
      if (!isLoadingRef.current) return;

      const distance = Math.sqrt(
        Math.pow(e.clientX - lastMouseXRef.current, 2) + Math.pow(e.clientY - lastMouseYRef.current, 2)
      );

      if (distance > LOADER_CONFIG.mouseDistance) {
        const newTime = createEmoji(e.clientX, e.clientY);
        lastEmojiTimeRef.current = newTime;
        lastMouseXRef.current = e.clientX;
        lastMouseYRef.current = e.clientY;
      }
    };

    const onLoaderComplete = () => {
      isLoadingRef.current = false;
      
      // Heractiveer scrollen wanneer loader 100% is
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      
      // Verwijder event listener
      if (LOADER_CONFIG.emojiEnabled) {
        document.removeEventListener("mousemove", handleMouseMove);
      }
      
      // Call callback als die bestaat
      if (onComplete) {
        onComplete();
      }
    };

    // Rotatie animatie
    gsap.to(".loader", {
      rotation: "+=180",
      duration: 1.5,
      delay: 1,
      repeat: LOADER_CONFIG.rotationRepeat,
      ease: "power4.inOut",
      onComplete: () => {
        // Scale animatie na rotatie
        gsap.to(".loader", {
          scale: 0,
          duration: 2,
          ease: "power4.inOut",
          onComplete: onLoaderComplete,
        });
      },
    });

    // Preloader verdwijnt na totale delay
    gsap.to(".preloader", {
      clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
      duration: 1.5,
      delay: LOADER_CONFIG.loaderDelay,
      ease: "power4.inOut",
    });

    // Emoji functionaliteit (optioneel)
    if (LOADER_CONFIG.emojiEnabled) {
      document.addEventListener("mousemove", handleMouseMove);
    }

    return () => {
      // Cleanup: zorg ervoor dat scrollen altijd weer wordt geactiveerd
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      
      if (LOADER_CONFIG.emojiEnabled) {
        document.removeEventListener("mousemove", handleMouseMove);
      }
    };
  }, [onComplete]);

  return (
    <>
      <div className="preloader">
        <div className="loader"></div>
        <div className="loader-progress" style={{ position: 'absolute', bottom: '24px', left: '50%', transform: 'translateX(-50%)', color: '#262624', fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: '18px', letterSpacing: '-0.4px' }}>0%</div>
      </div>
      {LOADER_CONFIG.emojiEnabled && <div className="emojis"></div>}
    </>
  );
}
